import { StripeElementsOptions } from '@stripe/stripe-js';
import { useContext, useMemo } from 'react';

import { PortalConfigContext } from 'src/context/portalConfigContext';
import * as colors from 'src/theme/colors';

type StripeElementStyles = Pick<StripeElementsOptions, 'appearance' | 'fonts'>;

export const usePaymentElementStyles = (): StripeElementStyles => {
  const portalConfig = useContext(PortalConfigContext);
  const { clientAccentColor } = portalConfig.brand;

  const memoizedTheme = useMemo<StripeElementStyles>(() => {
    return {
      appearance: {
        theme: 'flat',
        variables: {
          colorText: colors.DarkFontColor,
          colorPrimaryText: colors.BlackHeadings,
          colorSecondaryText: colors.NonHoverBackground,
          colorBackground: colors.white,
          fontFamily: 'Inter',
          tabIconSelectedColor: colors.DarkFontColor,
          tabLogoColor: clientAccentColor,
          iconColor: clientAccentColor,
          borderRadius: '4px',
          iconCheckmarkColor: colors.white,
          iconMenuColor: colors.BlackHeadings,
        },
        rules: {
          '.Label': {
            color: colors.BlackHeadings,
            fontSize: '14px',
            fontWeight: '500',
          },
          '.Input': {
            borderColor: colors.NonHoverBorder,
            borderWidth: '1px',
            borderStyle: 'solid',
            padding: '9px 12px',
            color: colors.BlackHeadings,
          },
          '.Input::placeholder': {
            color: '#9B9FA3',
            fontSize: '16px',
          },
          '.Input:focus': {
            borderColor: colors.BlackHeadings,
            outline: 'none',
            boxShadow: 'none',
          },

          '.PickerItem': {
            borderColor: colors.NonHoverBorder,
            borderWidth: '1px',
            borderStyle: 'solid',
          },
          '.Tab': {
            borderColor: colors.NonHoverBorder,
            borderWidth: '1px',
            borderStyle: 'solid',
          },
          '.Tab:active': {
            outline: 'none',
            boxShadow: 'none',
          },
          '.Tab--selected': {
            borderColor: colors.BlackHeadings,
            borderWidth: '1px',
            borderStyle: 'solid',
            backgroundColor: colors.white,
            color: colors.BlackHeadings,
          },
          '.Tab--selected:focus ': {
            outline: 'none',
            boxShadow: 'none',
          },
          '.TabLabel--selected': {
            color: colors.BlackHeadings,
          },
          '.TabIcon': {
            fill: colors.GraySmall,
          },
          '.TabIcon--selected': {
            fill: '#000000',
          },
          '.TabLabel': {
            fontSize: '16px',
            fontWeight: '500',
            paddingTop: '8px',
          },
          '.TermsText': {
            color: colors.GraySmall,
            fontSize: '13px',
            fontWeight: '400',
          },

          '.RadioIconOuter--checked ': {
            stroke: colors.BlackHeadings,
          },
          '.RadioIconInner--checked': {
            stroke: colors.BlackHeadings,
            fill: colors.BlackHeadings,
          },
          '.CheckboxInput': {
            borderColor: colors.NonHoverBorder,
            borderWidth: '1px',
            borderStyle: 'solid',
          },
          '.CheckboxInput--checked': {
            borderColor: colors.BlackHeadings,
            backgroundColor: colors.BlackHeadings,
          },

          '.CheckboxLabel': {
            color: colors.BlackHeadings,
            fontSize: '14px',
            fontWeight: '500',
          },
          '.MenuIcon': {
            fill: colors.GraySmall,
          },
          '.MenuIcon:hover': {
            fill: colors.GraySmall,
          },
          '.MenuAction': {
            fill: colors.BlackHeadings,
          },

          '.Action': {
            color: colors.BlackHeadings,
          },
          '.PickerItem--highlight': {
            boxShadow: 'none',
            borderColor: colors.BlackHeadings,
          },
          '.PickerItem--highlight:hover,': {
            boxShadow: 'none',
          },
          '.AccordionItem': {
            padding: '16px',
            borderColor: colors.DividersAndCardBorders,
          },
          '.AccordionItem:last-child': {
            borderBottom: 'transparent',
          },
          '.AccordionItem--selected': {
            color: colors.BlackHeadings,
            borderColor: colors.DividersAndCardBorders,
          },
        },
      },
    };
  }, [clientAccentColor]);

  return memoizedTheme;
};
